import FormInput from "../../../../components/FormInput/FormInput";
import AnalysisSection from "../AnalysisSection/AnalysisSection";
import { WorkflowInfoSectionProps } from "./WorkflowInfoSection.types";
import "./WorkflowInfoSection.css"
import EllipsisMenu from "../../../../components/EllipsisMenu/EllipsisMenu";
import SkeletonLoadingView from "../../../../components/SkeletonLoadingView/SkeletonLoadingView";
import useEllipsisMenu from "../../../../components/EllipsisMenu/useEllipsisMenu";
import { useEffect, useState } from "react";
import Button from "../../../../components/Button/Button";
import useLoadingToast from "../../../../hooks/useLoadingToast";
import SectionType from "../../../../enums/sectionType";
import ErrorView from "../SectionError/ErrorView";
import { AngularSectionScan, PatchSectionScan } from "../../../../types/starAPIResponseTypes";
import WorkflowType from "../../../../enums/workflowType";
import PatchCornerType from "../../../../enums/patchCornerTypes";

const WorkflowInfoSection = (props: WorkflowInfoSectionProps) => {
  const { 
    workflow,
    sections,
    selectedSection,
    angularFilename,
    angularAssetId,
    angularAssetLength,
    angularAssetDiameter,
    angularAssetTemperature,
    angularWorkflowStart,
    angularWorkflowEnd,

    patchFilename,
    patchAssetId,
    patchAssetTemperature,
    isEditing,
    onEdit,
    onCancelEdit,
    onSubmit
   } = props

  const skeletonLoadingHeight = "6rem"

  const { ellipsisMenu } = useEllipsisMenu({
    items: [
      {
        item: "Edit",
        onClick: () => onEdit()
      }
    ]
  })

  const workflowState = workflow
  const sectionState = sections

  if (
    workflowState.status === "loading" || workflowState.status === "idle" ||
    sectionState.status === "loading" || sectionState.status === "idle"
  ) {
    return (
      <AnalysisSection title="Workflow">
        <>
          <div className="analysis-section">
            <SkeletonLoadingView style={{height: skeletonLoadingHeight}} />
          </div>

          <div className="workflow-info-grid">
            <div className="analysis-section">
              <SkeletonLoadingView style={{height: skeletonLoadingHeight}} />
            </div>

            <div className="analysis-section">
              <SkeletonLoadingView style={{height: skeletonLoadingHeight}} />
            </div>
          </div>
        </>
      </AnalysisSection>
    )
  }

  if (workflowState.status === "error" || sectionState.status === "error") {
    return (
      <AnalysisSection title="Workflow">
        <ErrorView 
          message={"Failed to load workflow information."}
        />
      </AnalysisSection>
    )
  }

  const workflowData = workflowState.data
  const sectionData = sectionState.data

  if (!workflowData || !sectionData) {
    return (
      <p>Error</p>
    )
  }

  if (workflowData.workflow_type === WorkflowType.ANGULAR) {
    return (
      <AnalysisSection title="Workflow" titleChild={
        <EllipsisMenu
          ellipsisMenu={ellipsisMenu}
        />
      }>
        <div className="analysis-section">
          <div className="workflow-info-container">
            <FormInput
              label={"Workflow name:"}
              input={angularFilename}
              disabled={!isEditing}
            />

            <FormInput
              label={"Asset ID:"}
              input={angularAssetId}
              disabled={!isEditing}
            />

            <FormInput
              label={"Asset Length:"}
              input={angularAssetLength}
              disabled={!isEditing}
            />

            <FormInput
              label={"Asset Diameter:"} 
              input={angularAssetDiameter}
              disabled={!isEditing}
            />
            
            <FormInput
              label={"Asset Temperature:"}
              input={angularAssetTemperature}
              disabled={!isEditing}
            />
            
            <FormInput
              label={"Asset Location:"} input={
                {
                  value: `${workflowData.location}`,
                  onChange: () => {},
                  placeholder: undefined,
                  error: undefined
                }
              }
              disabled={true}
            />
            
            <FormInput
              label={"Asset Type:"} input={
                {
                  value: `${workflowData.asset_type}`,
                  onChange: () => {},
                  placeholder: undefined,
                  error: undefined
                }
              }
              disabled={true}
            />
            
            <FormInput
              label={"Workflow Start:"}
              input={angularWorkflowStart}
              disabled={!isEditing}
            />
            
            <FormInput
              label={"Workflow End:"}
              input={angularWorkflowEnd}
              disabled={!isEditing}
            />
            
            {/* <FormInput
              label={"Tracing Cable:"} input={
                {
                  value: workflowData.has_tracing_cable ? "Yes" : "No",
                  onChange: () => {},
                  placeholder: undefined,
                  error: undefined
                }
              }
              disabled={true}
            /> */}
          </div>
          {isEditing && (
            <div className="edit-info-buttons">
              <Button title="Save" onClick={onSubmit} />
              <Button title="Cancel" onClick={onCancelEdit} />
            </div>
          )}
        </div>

        <div className="workflow-info-grid">
          <div className="analysis-section">
            <div className="workflow-info-container">
              <FormInput
                label={"Section Start:"} input={
                  {
                    value: `${(sectionData[selectedSection] as AngularSectionScan).section_start}`,
                    onChange: () => {},
                    placeholder: undefined,
                    error: undefined
                  }
                }
                disabled={true}
              />
              
              <FormInput
                label={"Section End:"} input={
                  {
                    value: `${(sectionData[selectedSection] as AngularSectionScan).section_end}`,
                    onChange: () => {},
                    placeholder: undefined,
                    error: undefined
                  }
                }
                disabled={true}
              />
              
              <FormInput
                label={"Section Type:"} input={
                  {
                    value: `${Object.values(SectionType)[(sectionData[selectedSection] as AngularSectionScan).angular_geometry_type]}`,
                    onChange: () => {},
                    placeholder: undefined,
                    error: undefined
                  }
                }
                disabled={true}
              />
              
              <FormInput
                label={"Pi360:"} input={
                  {
                    value: `${sectionData[selectedSection].device_id}`,
                    onChange: () => {},
                    placeholder: undefined,
                    error: undefined
                  }
                }
                disabled={true}
              />
            </div>
          </div>

          <div className="analysis-section">
            <div className="workflow-info-container">
              <FormInput
                label={"Cladding Material:"} input={
                  {
                    value: workflowData.cladding_material,
                    onChange: () => {},
                    placeholder: undefined,
                    error: undefined
                  }
                }
                disabled={true}
              />
              
              <FormInput
                label={"Cladding Thickness:"} input={
                  {
                    value: `${workflowData.cladding_thickness}`,
                    onChange: () => {},
                    placeholder: undefined,
                    error: undefined
                  }
                }
                disabled={true}
              />
                
                <FormInput
                label={"Insulation Material:"} input={
                  {
                    value: workflowData.insulation_material,
                    onChange: () => {},
                    placeholder: undefined,
                    error: undefined
                  }
                }
                disabled={true}
              />
              
              <FormInput
                label={"Insulation Thickness:"} input={
                  {
                    value: `${workflowData.insulation_thickness}`,
                    onChange: () => {},
                    placeholder: undefined,
                    error: undefined
                  }
                }
                disabled={true}
              />
            </div>
          </div>
        </div>
      </AnalysisSection>
    )
  }

  if (workflowData.workflow_type === WorkflowType.PATCH) {
    return (
      <AnalysisSection title="Workflow" titleChild={
        <EllipsisMenu
          ellipsisMenu={ellipsisMenu}
        />
      }>
        <div className="analysis-section">
          <div className="workflow-info-container">
            <FormInput
              label={"Workflow name:"}
              input={patchFilename}
              disabled={!isEditing}
            />

            <FormInput
              label={"Asset ID:"}
              input={patchAssetId}
              disabled={!isEditing}
            />
            
            <FormInput
              label={"Asset Temperature:"}
              input={patchAssetTemperature}
              disabled={!isEditing}
            />
            
            <FormInput
              label={"Asset Location:"} input={
                {
                  value: `${workflowData.location}`,
                  onChange: () => {},
                  placeholder: undefined,
                  error: undefined
                }
              }
              disabled={true}
            />
            
            <FormInput
              label={"Asset Type:"} input={
                {
                  value: `${workflowData.asset_type}`,
                  onChange: () => {},
                  placeholder: undefined,
                  error: undefined
                }
              }
              disabled={true}
            />
          </div>
          {isEditing && (
            <div className="edit-info-buttons">
              <Button title="Save" onClick={onSubmit} />
              <Button title="Cancel" onClick={onCancelEdit} />
            </div>
          )}
        </div>

        <div className="workflow-info-grid">
          <div className="analysis-section">
            <div className="workflow-info-container">
          
              <FormInput
                label={"Patch Width:"} input={
                  {
                    value: `${(sectionData[selectedSection] as PatchSectionScan).width}`,
                    onChange: () => {},
                    placeholder: undefined,
                    error: undefined
                  }
                }
                disabled={true}
              />
              
              <FormInput
                label={"Patch Height:"} input={
                  {
                    value: `${(sectionData[selectedSection] as PatchSectionScan).height}`,
                    onChange: () => {},
                    placeholder: undefined,
                    error: undefined
                  }
                }
                disabled={true}
              />
              
              <FormInput
                label={"Corner Type:"} input={
                  {
                    value: `${Object.values(PatchCornerType)[(sectionData[selectedSection] as PatchSectionScan).scan_corner_type]}`,
                    onChange: () => {},
                    placeholder: undefined,
                    error: undefined
                  }
                }
                disabled={true}
              />
              
              <FormInput
                label={"Pi360:"} input={
                  {
                    value: `${sectionData[selectedSection].device_id}`,
                    onChange: () => {},
                    placeholder: undefined,
                    error: undefined
                  }
                }
                disabled={true}
              />
            </div>
          </div>

          <div className="analysis-section">
            <div className="workflow-info-container">
              <FormInput
                label={"Cladding Material:"} input={
                  {
                    value: workflowData.cladding_material ?? "",
                    onChange: () => {},
                    placeholder: undefined,
                    error: undefined
                  }
                }
                disabled={true}
              />
              
              <FormInput
                label={"Cladding Thickness:"} input={
                  {
                    value: `${workflowData.cladding_thickness}`,
                    onChange: () => {},
                    placeholder: undefined,
                    error: undefined
                  }
                }
                disabled={true}
              />
                
                <FormInput
                label={"Insulation Material:"} input={
                  {
                    value: workflowData.insulation_material ?? "",
                    onChange: () => {},
                    placeholder: undefined,
                    error: undefined
                  }
                }
                disabled={true}
              />
              
              <FormInput
                label={"Insulation Thickness:"} input={
                  {
                    value: `${workflowData.insulation_thickness}`,
                    onChange: () => {},
                    placeholder: undefined,
                    error: undefined
                  }
                }
                disabled={true}
              />
            </div>
          </div>
        </div>
      </AnalysisSection>
    )
  }

  return (
    <ErrorView message="Invalid workflow type." />
  )
}

export default WorkflowInfoSection